// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React, { useEffect, useState } from "react";

import {
  getFhirId,
  isUserRegistered,
  logout as firebaseLogout,
  onIdTokenChanged,
  setFirebasePersistenceLocal,
} from "../../services/firebase";
import { login as firebaseLogin } from "../../services/login";
import { noop } from "../../util";
import { isExpired } from "../../util/date";
import errorHandler from "../../util/errorHandler";
import { AuthContext } from "./AuthContext";
import { maybeUnknownState } from "./maybeUnknownState";
import { usePatient } from "./usePatient";
import { userStates } from "./userStates";

const expiryMinutes = process.env.REACT_APP_GOOGLE_AUTH_EXPIRY_MINUTES;

const persistTokenVerification = (isVerified) => {
  const encodedTokenVerification = window.btoa(isVerified);
  localStorage.setItem("customTokenVerified", encodedTokenVerification);
};

const retrieveTokenVerification = () => {
  const tokenVerified = localStorage.getItem("customTokenVerified");
  return tokenVerified ? window.atob(tokenVerified) : "unknown";
};

const initialState = {
  currentUser: undefined,
  customTokenVerified: retrieveTokenVerification(),
  hasHealthStatus: maybeUnknownState.UNKNOWN,
  logged: userStates.UNKNOWN,
  patientConsent: maybeUnknownState.UNKNOWN,
  patientData: undefined,
  registered: false,
  userId: undefined,
};

export const AuthProvider = ({ children }) => {
  const [userState, setUserState] = useState(initialState);
  const [userRegistered, setUserRegistered] = useState(false);
  const {
    hasHealthStatus,
    loading,
    onPatientDataLoad,
    patientConsent,
    patientData,
    refetch: refetchPatientData,
    setCalled,
  } = usePatient({
    registered: userRegistered,
  });
  const [isCustomTokenLogin, setIsCustomTokenLogin] = useState(false);
  const [isGoogleLogin, setIsGoogleLogin] = useState(false);
  const [loginCallback, setLoginCallback] = useState(() => noop);

  useEffect(() => {
    if (userState.logged === userStates.LOGGED_IN) {
      loginCallback?.();
    }
  }, [loginCallback, userState.logged]);

  const login = async ({
    credentials,
    callback,
    loginProvider,
    loginSource,
  }) => {
    setLoginCallback(() => callback);
    await firebaseLogin({ loginProvider, credentials, loginSource });
  };

  const updateCurrentUser = (user, registered, userId) =>
    setUserState((prevState) => ({
      ...prevState,
      currentUser: user,
      customTokenVerified: retrieveTokenVerification(),
      logged: user ? userStates.LOGGED_IN : userStates.LOGGED_OUT,
      registered,
      userId,
    }));

  const updateCustomTokenVerified = (isVerified) => {
    persistTokenVerification(isVerified);

    setUserState((prevState) => ({
      ...prevState,
      customTokenVerified: isVerified,
    }));
  };

  const logout = async () => {
    localStorage.removeItem("healthQuestionnaireSubmitted");
    setUserRegistered(false);
    setUserState({
      ...initialState,
      logged: userStates.LOGGED_OUT,
    });
    setCalled(false);
    return firebaseLogout();
  };

  useEffect(() => {
    const checkGoogleUserExpired = async (user) => {
      const token = await user?.getIdTokenResult();
      setIsGoogleLogin(token?.signInProvider === "google.com");

      if (isGoogleLogin && isExpired(expiryMinutes, token?.authTime)) {
        await logout();
        return null;
      }
      return user;
    };

    const checkCustomLogin = async (user) => {
      const token = await user?.getIdTokenResult();
      setIsCustomTokenLogin(token?.signInProvider === "custom");
    };

    onIdTokenChanged(async (user) => {
      const checkedUser = await checkGoogleUserExpired(user);
      checkCustomLogin(user);
      const registered = await isUserRegistered(checkedUser);
      setUserRegistered(registered);
      const fhirId = await getFhirId(checkedUser);
      errorHandler.setUser(fhirId);
      updateCurrentUser(checkedUser, registered, fhirId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUserState((prevState) => ({
      ...prevState,
      hasHealthStatus,
      patientConsent,
      patientData,
    }));

    if (userRegistered) {
      setFirebasePersistenceLocal();
    }
  }, [hasHealthStatus, patientConsent, patientData, userRegistered]);

  return (
    <AuthContext.Provider
      value={{
        isCustomTokenLogin,
        isGoogleLogin,
        loading,
        login,
        logout,
        onPatientDataLoad,
        refetchPatientData,
        updateCustomTokenVerified,
        userState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
